<script lang="ts">
	import type { CarResult } from './types';
	import VehicleErrorImage from '$assets/itinerary/vehicle-error.svg';
	import { env } from '$env/dynamic/public';
	import Button from '$lib/components/atoms/button/Button.svelte';
	import CarCard from '$lib/components/itinerary/options/cars/CarCard.svelte';
	import RegisterOrEditCarButton from '$lib/components/itinerary/options/cars/RegisterOrEditCarButton.svelte';
	import RentCarCard from '$lib/components/itinerary/options/cars/RentCarCard.svelte';
	import { getMapLayoutContext } from '$lib/components/map/map-layout-context';
	import { getAdvertiserData } from '$lib/constants/advertising';
	import * as c from '$lib/i18n/computedMessages';
	import { track } from '$lib/services/tracking/googleAnalytics';
	import { getSearchParamsStore, gotoWithParams } from '$lib/services/urls/searchParams';
	import * as m from '$paraglide/messages';

	interface Props {
		selectedCarId: string | undefined;
		carsList: CarResult[];
		hasError: boolean;
		isLoading: boolean;
		showRegisterCar?: boolean;
		onlyEv: boolean;
		onRetry?: () => void;
		onSelectCar?: (car: CarResult) => void;
	}

	const {
		selectedCarId,
		carsList,
		hasError,
		isLoading,
		showRegisterCar = false,
		onlyEv,
		onRetry,
		onSelectCar,
	}: Props = $props();

	const isRegisterMyCarFeatureActive = env.PUBLIC_FEATURE_REGISTER_MY_CAR_ENABLED === 'true';
	const isRegisterEVFeatureActive = env.PUBLIC_FEATURE_REGISTER_EV_ENABLED === 'true';

	const searchParams = getSearchParamsStore(false);

	const { tripOptionsStore } = getMapLayoutContext();

	const handleRegisterOrEditCar = () => {
		track({
			action: 'car_fill_start',
			payload: {
				action: 'click',
			},
		});
		gotoWithParams('/routes/search-car', { ...$searchParams });
	};
	const advertiserConfiguration = getAdvertiserData();

	const showRegisterOrEditCarButton
		= $derived((isRegisterMyCarFeatureActive && !onlyEv) || (isRegisterEVFeatureActive && onlyEv));

	const showRegisterOrEditCarEVButton = $derived(onlyEv);
</script>

{#if hasError}
	<div class="flex flex-col items-center gap-2 text-center py-4">
		<img src={VehicleErrorImage} alt="error" class="h-20 w-20" />
		{m.itinerary__options__cars__error()}
		<Button
			onclick={() => onRetry?.()}
			text={m.itinerary__options__cars__retry()}
			icon="autorenew"
		/>
	</div>
{:else}
	<div class="car-grid">
		{#if isLoading}
			{#each { length: 4 } as _, index (index)}
				<CarCard isLoading />
			{/each}
		{:else}
			{#each carsList as car (car.id)}
				{@const infos = onlyEv
					? car.consumptionLabels?.find(
						({ distanceSystem }) => distanceSystem === $tripOptionsStore.distanceSystem,
					)?.value || ''
					: c.carEnergies(car.energy)}
				<CarCard
					title={car.name}
					{infos}
					badge={car.isDefault && car.type
						? c.carTypes(car.type)
						: m.itinerary__options__car_badge__user()}
					selected={car.id === selectedCarId}
					onclick={() => onSelectCar?.(car)}
				/>
			{/each}
			{#if advertiserConfiguration?.carCard}
				{@const trackingName = advertiserConfiguration.carCard.trackingName}
				<RentCarCard {...advertiserConfiguration.carCard}
					onMount={() =>
						track({
							action: 'add_interactions',
							payload: { action: 'display', add_type: 'card_option', add_advertiser: trackingName },
						})}
					onCtaClick={() =>
						track({
							action: 'add_interactions',
							payload: { action: 'click', add_type: 'card_option', add_advertiser: trackingName },
						})}
				/>
			{/if}
			{#if showRegisterOrEditCarButton}
				<RegisterOrEditCarButton
					icon={showRegisterCar ? 'add' : 'edit'}
					text={showRegisterCar
						? m.itinerary__options__car_badge__register()
						: m.itinerary__options__car_badge__edit()}
					isTransparent={showRegisterCar}
					onclick={handleRegisterOrEditCar}
				/>
			{:else if showRegisterOrEditCarEVButton}
				<RegisterOrEditCarButton
					badgeText={m.soon()}
					text={m.itinerary__options__car_badge__register()}
					isTransparent={showRegisterCar}
					disabled
				/>
			{/if}
		{/if}
	</div>
{/if}

<style>
	.car-grid {
		display: grid;
		grid-template-columns: repeat(2, minmax(140px, 1fr));
		grid-gap: 8px;
		grid-auto-rows: 1fr;
	}
</style>
